import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, formatApiError } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSetSelectTheme } from "@/contexts/SelectThemeContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { US_STATES } from "@/lib/states";
import ShippingRatePicker from "@/components/ShippingRatePicker";
import { toast } from "sonner";
import { Lock } from "lucide-react";

export default function Checkout() {
  const { items, subtotal, count, MIN_KITS, meetsMinimum, FREE_SHIP_THRESHOLD, freeShipping, freeShipRemaining, shipping, total, cartTier, isSelectCart, cartIs10vk } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [newAddr, setNewAddr] = useState({ label: "Home", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" });
  const [payment, setPayment] = useState("venmo");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState(null); // { code, discount }
  const [codeError, setCodeError] = useState("");
  const [localDelivery, setLocalDelivery] = useState(false);
  // Research-use acknowledgement — required on every checkout (both Supply
  // and Select). Persisted on the order as `research_ack: true` for audit.
  const [researchAck, setResearchAck] = useState(false);
  const [ackError, setAckError] = useState(false);
  // Real-time shipping quote (Shippo). Null until buyer picks (or the picker
  // auto-selects the cheapest). When null AND Shippo is configured, we
  // block submission — every EP-enabled checkout must resolve to a rate.
  const [pickedShipping, setPickedShipping] = useState(null);
  // iter221 — Paid-ship confirmation. When the buyer qualifies for free
  // shipping but picks a non-free service (Ground or faster), gate the
  // Place-order click behind a confirmation that shows the actual charge.
  const [pendingConfirm, setPendingConfirm] = useState(null); // null | { rate_dollars, service_display }

  const applyCode = async () => {
    setCodeError("");
    if (!codeInput.trim()) return;
    try {
      const { data } = await api.post("/discount-codes/validate", { code: codeInput, subtotal, cart_tier: cartTier });
      setAppliedCode(data);
      toast.success(`Code ${data.code} applied — $${data.discount.toFixed(2)} off`);
    } catch (err) {
      setAppliedCode(null);
      setCodeError(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code");
    }
  };

  const discount = appliedCode?.discount || 0;
  const discountedSubtotal = Math.max(0, subtotal - discount);
  // Shipping resolution order (must match server/orders.py logic):
  //   1. Local delivery → 0
  //   2. 10VK cart is NEVER free (owner directive 2026-10-12)
  //   3. Free-ship threshold hit AND picked service is USPS Priority Mail
  //      or UPS 2nd Day Air → 0
  //   4. Real-time picked rate → its rate
  //   5. Fallback flat rate (used when Shippo is unconfigured or ZIP missing)
  const pickedRateDollars = pickedShipping ? pickedShipping.rate_cents / 100 : null;
  // Free-eligible services (owner directive 2026-10-12): USPS Priority Mail
  // AND UPS 2nd Day Air only. Ground stays paid.
  const isFreeEligibleService = pickedShipping && (
    pickedShipping.service === "usps_priority"
    || pickedShipping.service === "ups_second_day_air"
  );
  const qualifiesForFreeShip = !cartIs10vk && FREE_SHIP_THRESHOLD != null && discountedSubtotal >= FREE_SHIP_THRESHOLD;
  let effectiveShipping;
  let missingShipping = false;
  if (localDelivery) {
    effectiveShipping = 0;
  } else if (pickedRateDollars != null) {
    effectiveShipping = (isFreeEligibleService && qualifiesForFreeShip) ? 0 : pickedRateDollars;
  } else {
    // No flat fallback — customer must pick a live USPS/UPS rate before
    // placing the order. Order Summary shows a dash until a rate is picked
    // and the "Place order" button is disabled with a shipping-required note.
    effectiveShipping = 0;
    missingShipping = true;
  }
  const finalTotal = discountedSubtotal + effectiveShipping;

  useEffect(() => {
    if (submitted) return;
    if (items.length === 0) nav("/cart", { replace: true });
    else if (!meetsMinimum) nav("/cart", { replace: true });
  }, [items, meetsMinimum, nav, submitted]);

  // iter156 — clear any picked Shippo rate when the buyer flips to Local
  // Delivery. Without this, a stale `pickedShipping` quote_id can be sent
  // alongside `local_delivery: true`, causing the backend to try to resolve
  // an expired/orphan quote and 409 (or 5xx if the resolve throws) even
  // though local delivery zeroes shipping anyway. This eliminates the
  // race that produced a "Something went wrong" toast for at least one
  // customer on the Card-payment + Local Delivery path.
  useEffect(() => {
    if (localDelivery && pickedShipping) setPickedShipping(null);
  }, [localDelivery, pickedShipping]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/addresses");
      setAddresses(data);
      if (data.length > 0) {
        const def = data.find((a) => a.is_default) || data[0];
        setSelectedAddressId(def.id);
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    // Research-use acknowledgement gate — required on every order regardless
    // of tier so we have a per-order audit trail.
    if (!researchAck) {
      setAckError(true);
      toast.error("Please confirm the research-use acknowledgement to continue.");
      // Scroll the checkbox into view so the user sees what's blocking them.
      document.querySelector('[data-testid="research-ack-checkbox"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    // Live-shipping gate — every non-local-delivery order MUST resolve to a
    // real USPS/UPS rate before submit. The flat $22 fallback was removed
    // in the 2026-02-16 site-optimization pass.
    if (!localDelivery && !pickedShipping) {
      toast.error("Please pick a shipping option before placing the order.");
      document.querySelector('[data-testid="shipping-rate-picker"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    // iter221 — Paid-ship confirmation gate. If the buyer qualifies for free
    // shipping but picked a paid (non-free) service, surface a confirmation
    // modal showing the exact charge before placing the order.
    if (
      !localDelivery
      && pickedShipping
      && qualifiesForFreeShip
      && !isFreeEligibleService
    ) {
      setPendingConfirm({
        rate_dollars: pickedRateDollars,
        service_display: pickedShipping.service_display,
      });
      return;
    }
    await _doSubmit();
  };

  const _doSubmit = async () => {
    setBusy(true);
    setSubmitted(true);
    try {
      const payload = {
        items,
        payment_method: payment,
        discount_code: appliedCode?.code || null,
        local_delivery: localDelivery,
        // Local pick-up is always pickup mode now — the ZIP guardrail was
        // removed because the "Springfield/Nashville only" label already
        // sets the expectation. Some buyers legitimately live across the
        // state line but do business in Springfield, so ZIP-based blocking
        // was creating false negatives.
        local_delivery_mode: localDelivery ? "pickup" : null,
        pickup_notes: null,
        research_ack: true,
        ...(pickedShipping ? { shipping_quote_id: pickedShipping.quote_id, shipping_option_id: pickedShipping.option_id } : {}),
        ...(selectedAddressId ? { address_id: selectedAddressId } : { address: newAddr }),
      };
      const { data } = await api.post("/orders", payload);
      // NOTE: Do NOT clear the cart here. Cart is cleared only after actual
      // payment success (PaymentSuccess.jsx for Stripe; "I've sent payment"
      // click in ManualPayment.jsx for Venmo/Cash App). This preserves cart
      // state if the buyer bails from the payment page or hits Back — real
      // customer feedback drove this fix.
      // For Stripe, kick off embedded checkout on the payment page (buyer stays on our domain)
      nav(`/payment/${data.id}`, { replace: true });
    } catch (err) {
      setSubmitted(false);
      // iter156 — smart error surfacing. Distinguishes 400 (specific detail),
      // 5xx (server error / no detail), and network failures. Raw error also
      // gets dumped to browser console so screenshots of DevTools give us
      // real evidence next time an order silently fails.
      toast.error(formatApiError(err, "Order failed — please try again."));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "mt-2 rounded-none border-0 border-b border-borderSubtle bg-transparent focus-visible:ring-0 focus-visible:border-oxblood px-0";

  // iter156.5 — tier-aware checkout theme. Read `isSelectCart` directly from
  // the cart (synchronous — no first-paint flash) AND flip the shared
  // `SelectThemeContext` so the Nav strip also swaps its wordmark + palette
  // to R-1617 for the duration of the checkout. `useSetSelectTheme` uses
  // useLayoutEffect + cleanup on unmount → navigating away resets the Nav.
  const isSelect = isSelectCart;
  useSetSelectTheme(isSelect);
  const theme = isSelect
    ? {
        shell: "bg-[#F1E5C7] text-[#1B1614] min-h-screen",
        h1: "text-[#1B1614]",
        eyebrow: "text-[#6E521A]",
        mutedText: "text-[#1B1614]/70",
        strongText: "text-[#1B1614]",
        cardActive: "border-[#DDA94E] bg-[#EBE0BE]",
        cardIdle: "border-[#D6C8A3] bg-[#EBE0BE]/60 hover:bg-[#EBE0BE]",
        aside: "bg-[#EBE0BE] border border-[#D6C8A3]",
        hairline: "border-[#D6C8A3]",
        input: "mt-2 rounded-none border-0 border-b border-[#D6C8A3] bg-transparent text-[#1B1614] placeholder:text-[#1B1614]/40 focus-visible:ring-0 focus-visible:border-[#DDA94E] px-0",
        codeInput: "flex-1 h-11 px-4 rounded-full border border-[#D6C8A3] text-[14px] bg-[#F1E5C7] text-[#1B1614] placeholder:text-[#1B1614]/40 focus:outline-none focus:border-[#DDA94E] font-mono uppercase tracking-wider",
        applyBtn: "px-5 h-11 bg-[#DDA94E] text-black text-[12px] font-semibold uppercase tracking-[0.22em] hover:bg-[#F1E5C7] transition-colors rounded-full",
        cta: "w-full py-[18px] bg-[#DDA94E] text-black tracking-[0.22em] text-[13px] md:text-[14px] font-semibold uppercase hover:bg-[#F1E5C7] transition-colors disabled:opacity-50 rounded-full",
        accent: "text-[#6E521A]",
        totalText: "text-[#6E521A]",
        ackIdle: "border-[#D6C8A3] hover:border-[#DDA94E]/60",
        ackChecked: "border-[#DDA94E]/60 bg-[#DDA94E]/[0.05]",
        localDelivHover: "hover:bg-[#F1E5C7] hover:border-[#D6C8A3]",
      }
    : {
        shell: "",
        h1: "text-charcoal",
        eyebrow: "text-oxblood",
        mutedText: "text-charcoal/85",
        strongText: "text-charcoal",
        cardActive: "border-oxblood bg-white",
        cardIdle: "border-borderSubtle bg-white/50 hover:bg-white",
        aside: "bg-white border border-borderSubtle",
        hairline: "border-borderSubtle",
        input: inputCls,
        codeInput: "flex-1 h-11 px-4 rounded-full border border-borderSubtle text-[14px] bg-white focus:outline-none focus:border-charcoal font-mono uppercase tracking-wider",
        applyBtn: "px-5 h-11 bg-charcoal text-bone text-[12px] font-semibold uppercase tracking-[0.22em] hover:bg-oxblood transition-colors rounded-full",
        cta: "w-full py-[18px] bg-oxblood text-bone tracking-[0.22em] text-[13px] md:text-[14px] font-semibold uppercase hover:bg-charcoal transition-colors disabled:opacity-50 rounded-full",
        accent: "text-oxblood",
        totalText: "text-oxblood",
        ackIdle: "border-borderSubtle hover:border-charcoal/40",
        ackChecked: "border-charcoal/40 bg-charcoal/[0.02]",
        localDelivHover: "hover:bg-bone hover:border-borderSubtle",
      };

  return (
    <div className={theme.shell} data-testid={isSelect ? "checkout-page-select" : "checkout-page-supply"}>
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-14 lg:py-20" data-testid="checkout-page">
      <p className={`overline mb-4 ${theme.eyebrow}`}>Step 2 of 3</p>
      <h1 className={`font-serif text-5xl md:text-6xl tracking-tight mb-12 ${theme.h1}`}>Checkout.</h1>

      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-12">
          <section>
            <p className={`overline mb-6 ${theme.eyebrow}`}>Shipping to · {user?.name || user?.email}</p>

            {addresses.length > 0 && (
              <div className="space-y-3 mb-6" data-testid="saved-addresses">
                {addresses.map((a) => (
                  <label key={a.id} className={`flex items-start gap-4 border p-4 cursor-pointer transition-colors ${selectedAddressId === a.id ? theme.cardActive : theme.cardIdle}`}>
                    <input
                      type="radio" name="addr" value={a.id}
                      checked={selectedAddressId === a.id}
                      onChange={() => setSelectedAddressId(a.id)}
                      className={`mt-1 ${isSelect ? "accent-[#DDA94E]" : "accent-oxblood"}`}
                      data-testid={`address-radio-${a.id}`}
                    />
                    <div className="text-[15px]">
                      <p className={`font-semibold ${theme.strongText}`}>{a.label}</p>
                      <p className={theme.mutedText}>{a.line1}{a.line2 ? `, ${a.line2}` : ""}</p>
                      <p className={theme.mutedText}>{a.city}, {a.state} {a.postal_code}</p>
                    </div>
                  </label>
                ))}
                <button type="button" onClick={() => setSelectedAddressId("")}
                        className={`overline ${theme.accent} hover:opacity-80`}
                        data-testid="use-new-address-btn">
                  {selectedAddressId ? "Use a new address →" : "Adding new address"}
                </button>
              </div>
            )}

            {!selectedAddressId && (
              <div className="grid md:grid-cols-2 gap-6" data-testid="new-address-form">
                <div className="md:col-span-2">
                  <Label className={`overline ${theme.eyebrow}`}>Address line 1</Label>
                  <Input required value={newAddr.line1} onChange={(e) => setNewAddr({ ...newAddr, line1: e.target.value })} className={theme.input} data-testid="addr-line1"/>
                </div>
                <div className="md:col-span-2">
                  <Label className={`overline ${theme.eyebrow}`}>Address line 2</Label>
                  <Input value={newAddr.line2} onChange={(e) => setNewAddr({ ...newAddr, line2: e.target.value })} className={theme.input} data-testid="addr-line2"/>
                </div>
                <div>
                  <Label className={`overline ${theme.eyebrow}`}>City</Label>
                  <Input required value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} className={theme.input} data-testid="addr-city"/>
                </div>
                <div>
                  <Label className={`overline ${theme.eyebrow}`}>State / Region</Label>
                  <Select value={newAddr.state} onValueChange={(v) => setNewAddr({ ...newAddr, state: v })}>
                    <SelectTrigger className={`mt-2 rounded-none border-0 border-b bg-transparent px-0 h-11 focus:ring-0 ${isSelect ? "border-[#D6C8A3] text-[#1B1614] focus:border-[#DDA94E]" : "border-borderSubtle focus:border-oxblood"}`} data-testid="addr-state">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {US_STATES.map((s) => (
                        <SelectItem key={s.code} value={s.code}>{s.code} — {s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={`overline ${theme.eyebrow}`}>Postal code</Label>
                  <Input required value={newAddr.postal_code} onChange={(e) => setNewAddr({ ...newAddr, postal_code: e.target.value })} className={theme.input} data-testid="addr-postal"/>
                </div>
                <div>
                  <Label className={`overline ${theme.eyebrow}`}>Country</Label>
                  <Input required value={newAddr.country} onChange={(e) => setNewAddr({ ...newAddr, country: e.target.value })} className={theme.input} data-testid="addr-country"/>
                </div>
              </div>
            )}
          </section>

          {/* Real-time shipping rate picker — auto-fetches from Shippo when
              destination ZIP is valid. If Shippo isn't configured yet, the
              picker hides itself and the flat-rate fallback in the total
              summary takes over (matches backend behavior). */}
          {/* iter156 — Live shipping rate picker HIDDEN entirely when Local
              Delivery is checked. Previously we just `disabled` the picker
              while leaving stale `pickedShipping` state around, which caused
              the payload to send `shipping_quote_id` alongside
              `local_delivery: true` and race with expired quotes on the
              server. Hiding it removes any ambiguity for the buyer AND
              guarantees no stale quote is sent. */}
          {!localDelivery && (
            <ShippingRatePicker
              items={items}
              destination={
                selectedAddressId
                  ? (() => {
                      const a = addresses.find((x) => x.id === selectedAddressId);
                      return a ? { zip: a.postal_code, street1: a.line1, city: a.city, state: a.state } : {};
                    })()
                  : { zip: newAddr.postal_code, street1: newAddr.line1, city: newAddr.city, state: newAddr.state }
              }
              onSelect={setPickedShipping}
              freeShippingOnGround={qualifiesForFreeShip}
            />
          )}

          <section>
            <p className={`overline mb-3 ${theme.eyebrow}`}>Payment method</p>
            {/* iter156.5 — Statement descriptor notice. Fires at the exact
                moment the buyer picks their payment method so there's no
                surprise on the credit-card bill (especially important for
                the discreet Select tier). Same copy on both tiers. */}
            <p className={`text-[13px] font-mono mb-6 flex items-center gap-2 ${theme.mutedText}`} data-testid="checkout-statement-note">
              <Lock size={12} strokeWidth={2.5} className={theme.accent} aria-hidden="true" />
              Purchases post to your statement as <span className={`font-semibold ${theme.accent}`}>BULKBUYKITS.COM</span>.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { id: "venmo", label: "Venmo", note: "Send to our handle" },
                { id: "cashapp", label: "Cash App", note: "Send to our $Cashtag" },
                { id: "stripe", label: "Card", note: "Visa · MC · Amex · Apple Pay" },
              ].map((p) => {
                const disabled = !!p.comingSoon;
                return (
                  <label
                    key={p.id}
                    className={`relative border p-5 transition-colors ${
                      disabled
                        ? (isSelect ? "border-[#D6C8A3] bg-[#F1E5C7] cursor-not-allowed opacity-60" : "border-borderSubtle bg-bone/50 cursor-not-allowed opacity-70")
                        : payment === p.id
                        ? (isSelect ? "border-[#DDA94E] bg-[#DDA94E]/[0.12] cursor-pointer" : `${theme.cardActive} cursor-pointer`)
                        : (isSelect ? "border-[#D6C8A3] bg-[#EBE0BE]/60 hover:border-[#DDA94E] hover:bg-[#DDA94E]/[0.20] duration-150 cursor-pointer" : `${theme.cardIdle} cursor-pointer`)
                    }`}
                    data-testid={`payment-option-${p.id}`}
                  >
                    {isSelect && !disabled && payment === p.id && (
                      <span
                        className="absolute top-2 right-2 inline-flex items-center gap-1 bg-black text-[#F1E5C7] text-[10px] uppercase tracking-[0.18em] font-mono font-bold px-2.5 py-1 rounded-full"
                        data-testid={`payment-selected-badge-${p.id}`}
                        aria-hidden="true"
                      >
                        ✓ Selected
                      </span>
                    )}
                    <input
                      type="radio" name="pay" value={p.id}
                      checked={!disabled && payment === p.id}
                      onChange={() => !disabled && setPayment(p.id)}
                      disabled={disabled}
                      className="sr-only"
                      data-testid={`payment-${p.id}`}
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-serif text-2xl ${disabled ? (isSelect ? "text-[#1B1614]/40" : "text-stone") : theme.strongText}`}>{p.label}</p>
                      {p.comingSoon && (
                        <span
                          className={`inline-block text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${isSelect ? "bg-[#DDA94E] text-black" : "bg-oxblood text-bone"}`}
                          data-testid={`coming-soon-${p.id}`}
                        >
                          Coming Soon
                        </span>
                      )}
                    </div>
                    <p className={`text-[14px] mt-1 ${theme.mutedText}`}>{p.note}</p>
                  </label>
                );
              })}
            </div>
            <p className={`text-[13px] font-mono mt-4 ${theme.mutedText}`}>Payment instructions and handles will appear after you place the order.</p>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <div className={`p-8 sticky top-24 rounded-2xl ${theme.aside}`}>
            <h2 className={`font-serif text-[26px] md:text-[30px] mb-6 ${theme.h1}`}>Order Summary</h2>

            {/* iter221 — Free-shipping progress indicator. Hidden for 10VK
                carts (never free). Shows "Unlocked" or "add $X more" against
                the FREE_SHIP_THRESHOLD. Uses discountedSubtotal to match the
                server-side free-ship compare. */}
            {!cartIs10vk && FREE_SHIP_THRESHOLD != null && items.length > 0 && (
              qualifiesForFreeShip ? (
                <div className={`mb-6 rounded-xl px-4 py-3 border-2 shadow-md ${isSelect ? "bg-charcoal border-[#DDA94E] text-bone" : "bg-oxblood border-oxblood text-bone"}`} data-testid="checkout-free-ship-unlocked">
                  <p className="text-[14px] font-semibold uppercase tracking-[0.14em]">
                    <span className={`inline-block mr-2 ${isSelect ? "text-[#DDA94E]" : "text-bone"}`}>✓</span>
                    Free USPS Priority Mail or UPS 2nd Day Air unlocked
                  </p>
                </div>
              ) : (
                <div className={`mb-6 rounded-xl px-4 py-3 border ${isSelect ? "bg-[#DDA94E]/[0.08] border-[#DDA94E]/40" : "bg-[#F5EFE3] border-[#D8C7A5]"}`} data-testid="checkout-free-ship-progress">
                  <p className={`text-[14px] ${isSelect ? "text-[#1B1614]" : "text-[#5A4A15]"}`}>
                    Add <span className="font-mono font-semibold" data-testid="checkout-free-ship-remaining">${Math.max(0, FREE_SHIP_THRESHOLD - discountedSubtotal).toFixed(2)}</span> more for FREE USPS Priority Mail or UPS 2nd Day Air.
                  </p>
                  <div className={`mt-2 h-1.5 w-full rounded-full ${isSelect ? "bg-[#2A2620]" : "bg-[#E8D8B4]"}`}>
                    <div
                      className={`h-1.5 rounded-full ${isSelect ? "bg-[#DDA94E]" : "bg-[#8A6D1E]"} transition-all`}
                      style={{ width: `${Math.min(100, (discountedSubtotal / FREE_SHIP_THRESHOLD) * 100).toFixed(1)}%` }}
                    />
                  </div>
                </div>
              )
            )}
            <div className="mb-5" data-testid="checkout-summary-items">
              {items.map((i) => {
                const kitSize = i.variant === "3vial" ? 3 : i.variant === "5vial" ? 5 : i.variant === "10vial" ? 10 : null;
                const is10vkKit = !i.variant && !i.is_byok_kit && Number(i.kit_vial_count) === 10;
                const isSingleVial = i.variant === "1vial" || (!i.variant && !i.is_byok_kit && !is10vkKit);
                let variantLabel;
                if (i.is_byok_kit) variantLabel = `BYO ${i.byok_size}-vial Kit`;
                else if (is10vkKit) variantLabel = "10-vial Kit";
                else if (kitSize) variantLabel = i.variant_mg ? `${i.variant_mg}mg · ${kitSize}-vial Kit` : `${kitSize}-vial Kit`;
                else if (isSingleVial) variantLabel = i.variant_mg ? `${i.variant_mg}mg · 1 vial` : "1 vial";
                else variantLabel = i.unit_label || "";
                return (
                  <div
                    key={`${i.product_id}::${i.variant || ""}::${i.variant_mg || ""}`}
                    className="flex justify-between gap-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className={`text-[14px] leading-tight truncate ${theme.strongText}`}>{i.name}</div>
                      <div className={`text-[12px] mt-0.5 ${theme.mutedText}`}>{variantLabel} × {i.quantity}</div>
                    </div>
                    <div className={`font-mono text-[14px] whitespace-nowrap ${theme.strongText}`}>
                      ${(i.price * i.quantity).toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={`my-4 border-t ${theme.hairline}`} />
            <div className="flex justify-between mb-2 text-[15px]">
              <span className={theme.mutedText}>Subtotal</span>
              <span className={`font-mono font-semibold ${theme.strongText}`}>${subtotal.toFixed(2)}</span>
            </div>
            {appliedCode && (
              <div className="flex justify-between mb-2 text-[15px]" data-testid="checkout-discount-row">
                <span className={`flex items-center gap-2 font-semibold ${isSelect ? "text-[#7BC97E]" : "text-[#2F7A31]"}`}>
                  Discount ({appliedCode.code})
                  <button type="button" onClick={() => { setAppliedCode(null); setCodeInput(""); }} className={`text-[13px] ${theme.mutedText} hover:opacity-80`} data-testid="remove-discount">✕</button>
                </span>
                <span className={`font-mono font-bold ${isSelect ? "text-[#7BC97E]" : "text-[#2F7A31]"}`}>−${discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between mb-4 text-[15px]">
              <span className={theme.mutedText}>
                Shipping{" "}
                {localDelivery
                  ? "(local pick-up)"
                  : pickedShipping
                    ? `(${pickedShipping.service_display})`
                    : "(pick an option)"}
              </span>
              <span className={`font-mono font-semibold ${theme.strongText}`} data-testid="checkout-shipping">
                {localDelivery
                  ? <span className={`font-bold ${isSelect ? "text-[#7BC97E]" : "text-[#2F7A31]"}`}>WAIVED</span>
                  : missingShipping
                    ? <span className={theme.mutedText}>—</span>
                    : effectiveShipping === 0
                      ? <span className={`font-bold ${isSelect ? "text-[#7BC97E]" : "text-[#2F7A31]"}`}>FREE</span>
                      : `$${effectiveShipping.toFixed(2)}`}
              </span>
            </div>

            {isSelect && (
              <div className="flex justify-between mb-4 text-[15px]" data-testid="checkout-payment-row">
                <span className={theme.mutedText}>Payment</span>
                <span className={`font-mono font-semibold ${theme.strongText}`} data-testid="checkout-payment-method">
                  {payment === "venmo" ? "Venmo" : payment === "cashapp" ? "Cash App" : payment === "stripe" ? "Card" : "—"}
                </span>
              </div>
            )}

            {/* Local-delivery honor-flag — Nashville-area buyers only. */}
            <label
              className={`flex items-start gap-3 mb-4 cursor-pointer p-3 rounded-md transition-colors border border-transparent ${theme.localDelivHover}`}
              data-testid="local-delivery-toggle"
            >
              <input
                type="checkbox"
                checked={localDelivery}
                onChange={(e) => setLocalDelivery(e.target.checked)}
                className={`mt-0.5 w-4 h-4 shrink-0 cursor-pointer ${isSelect ? "accent-[#DDA94E]" : "accent-oxblood"}`}
                data-testid="local-delivery-checkbox"
              />
              <div className="min-w-0">
                <p className={`text-[15px] font-semibold ${theme.strongText}`}>Local Pick-up (Springfield / Nashville only)</p>
                <p className={`text-[13px] leading-snug ${theme.mutedText}`}>
                  This waives shipping. Pick-up only — no delivery. We'll coordinate details after payment confirms.
                </p>
              </div>
            </label>

            {/* Discount code input */}
            {!appliedCode && (
              <div className="mb-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={codeInput}
                    onChange={(e) => { setCodeInput(e.target.value.toUpperCase()); setCodeError(""); }}
                    placeholder="Discount code"
                    className={theme.codeInput}
                    data-testid="discount-code-input"
                  />
                  <button
                    type="button"
                    onClick={applyCode}
                    className={theme.applyBtn}
                    data-testid="apply-code-btn"
                  >
                    Apply
                  </button>
                </div>
                {codeError && <p className={`text-[13px] mt-2 ${isSelect ? "text-[#6E521A]" : "text-oxblood"}`} data-testid="code-error">{codeError}</p>}
              </div>
            )}

            <div className={`my-5 border-t ${theme.hairline}`} />
            <div className="flex justify-between items-baseline mb-8">
              <span className={`font-serif font-medium ${theme.strongText} ${isSelect ? "text-[28px] md:text-[32px]" : "text-[24px] md:text-[28px]"}`}>Total</span>
              <span className={`font-mono font-bold ${theme.totalText} ${isSelect ? "text-[26px] md:text-[28px]" : "text-[22px] md:text-[24px]"}`} data-testid="checkout-total">${finalTotal.toFixed(2)}</span>
            </div>

            {/* Research-use acknowledgement — required on every order. Both
                Supply and Select tiers hit this gate so the admin has a clean
                per-order audit record. Larger hit target and clearer copy
                (2026-02-16 site-optimization pass). */}
            <label
              className={`flex items-start gap-3 mb-5 p-5 rounded-2xl border cursor-pointer transition-colors ${
                ackError && !researchAck
                  ? (isSelect ? "border-[#DDA94E] bg-[#DDA94E]/[0.08] animate-pulse" : "border-oxblood bg-oxblood/5 animate-pulse")
                  : researchAck
                    ? theme.ackChecked
                    : theme.ackIdle
              }`}
              data-testid="research-ack-label"
            >
              <input
                type="checkbox"
                checked={researchAck}
                onChange={(e) => { setResearchAck(e.target.checked); if (e.target.checked) setAckError(false); }}
                className={`mt-0.5 h-5 w-5 shrink-0 cursor-pointer ${isSelect ? "accent-[#DDA94E]" : "accent-oxblood"}`}
                data-testid="research-ack-checkbox"
              />
              <span className={`text-[14px] leading-relaxed ${theme.strongText}`}>
                I understand these products are for <span className="font-semibold">Research Use Only</span> and are not intended for human or animal consumption.
              </span>
            </label>

            <button
              type="submit" disabled={busy || (!localDelivery && !pickedShipping)}
              className={theme.cta}
              data-testid="place-order-btn"
            >
              {busy ? "Placing…" : "Place order"}
            </button>
            {!localDelivery && !pickedShipping && (
              <p className={`text-[12px] font-mono uppercase tracking-[0.18em] mt-3 text-center ${theme.mutedText}`} data-testid="shipping-required-note">
                Pick a shipping option to continue
              </p>
            )}
          </div>
        </aside>
      </form>

      {/* iter221 — Paid-ship confirmation modal. Fires when buyer qualifies
          for free shipping but has picked a non-free service. Shows the
          actual charge and the free alternative so the choice is explicit. */}
      {pendingConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          onClick={() => setPendingConfirm(null)}
          data-testid="paid-ship-confirm-modal"
        >
          <div
            className={`w-full max-w-md rounded-2xl p-7 shadow-2xl ${isSelect ? "bg-[#F1E5C7] border border-[#D6C8A3]" : "bg-white border border-borderSubtle"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className={`font-serif text-[22px] mb-3 ${theme.h1}`}>You qualify for FREE shipping</h3>
            <p className={`text-[14px] leading-relaxed mb-4 ${theme.mutedText}`}>
              Your order qualifies for FREE USPS Priority Mail or UPS 2nd Day Air. You've selected{" "}
              <span className={`font-semibold ${theme.strongText}`}>{pendingConfirm.service_display}</span>, which costs{" "}
              <span className={`font-mono font-bold ${theme.strongText}`} data-testid="paid-ship-confirm-rate">${pendingConfirm.rate_dollars.toFixed(2)}</span>.
            </p>
            <p className={`text-[13px] mb-6 ${theme.mutedText}`}>
              Would you like to keep this paid option, or switch to a FREE option?
            </p>
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() => { setPendingConfirm(null); _doSubmit(); }}
                className={theme.cta}
                data-testid="paid-ship-confirm-continue"
              >
                Keep {pendingConfirm.service_display} · ${pendingConfirm.rate_dollars.toFixed(2)}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPendingConfirm(null);
                  document.querySelector('[data-testid="shipping-picker-options"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                className={`w-full py-3 text-[13px] font-semibold uppercase tracking-[0.22em] rounded-full border transition-colors ${isSelect ? "border-[#D6C8A3] text-[#1B1614] hover:bg-[#EBE0BE]" : "border-borderSubtle text-charcoal hover:bg-bone"}`}
                data-testid="paid-ship-confirm-switch"
              >
                Pick a FREE option
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
